import classnames from 'classnames';
import type { FC } from 'react';
import { memo, useCallback, useState } from 'react';

import { useAppApolloClient } from '../../hooks/useAppApolloClient';
import { useAppSelector } from '../../redux/hooks';
import type { VoteRef } from '../../services/scrollSections';
import { useScrollSectionsHelpers } from '../../services/scrollSections';
import type { Message } from '../../types/api';
import { isBrowser, isMobile } from '../../utils/web';
import type { CreateOrUpdateMessageVariablesSmall } from '../api/message';
import { ThreadInteractiveMessage } from '../Thread/ThreadInteractiveMessage';
import { useThreadContext } from '../ThreadContexts/ThreadContext';
import { useMessageEditing } from '../ThreadContexts/ThreadEditingContext';
import { ThreadEditorForm } from '../Write/ThreadEditorForm';
import { submitAnswer as submitMessage, useMessageRedux } from './thread-service';
import { messagesStateToChildMessagesIds } from './thread-slice';

interface ThreadMessageBlockProps {
  messageId: string | undefined;
  isNestedAnswer?: boolean;
  targetedId?: string;
  unlimited?: boolean;
  // `isBestAnswer` n'a plus de consommateur depuis le retrait de `MessageLd` (B1_01, D10) : son
  // unique lecteur était le JSON-LD par message, éteint de longue date. La prop reste déclarée ici
  // (et dans `threadTypes.ts` / `ThreadMessageRoot.tsx`) — aucun appelant ne la passe aujourd'hui ;
  // son retrait des trois interfaces sort du périmètre de ce ticket.
  isBestAnswer?: boolean; // Missing feature?
  hide?: boolean;
  voteRef: VoteRef;
  onAnswer: VoidFunction;
  onDelete?: (message: Message) => void;
  isYakliDisplay?: boolean;
}

// The authentication needs to be already done (you can use usePermissions to check it) to ensure the isEditing state is well initialized. Reason: useMessageEditing's result depends on the current user, thus the value can change after the authentication.

export const ThreadMessageAnswerBlock: FC<ThreadMessageBlockProps> = memo(
  function ThreadMessageAnswerBlock(props) {
    const {
      messageId,
      isNestedAnswer,
      targetedId,
      unlimited,
      hide,
      voteRef,
      onAnswer,
      onDelete,
      isYakliDisplay,
    } = props;

    const client = useAppApolloClient();
    const { slug } = useThreadContext();

    const message = useMessageRedux(messageId, isYakliDisplay);

    const isTargeted = targetedId === message?.id;
    const isEditingFromQuery = useMessageEditing();

    const [isEditing, setIsEditing] = useState(!!(isTargeted && isEditingFromQuery));

    const { forceCurrentScrollSection } = useScrollSectionsHelpers();

    const contentEditorId = `edit-message-${message?.id}`;

    const startEditing = useCallback(() => {
      if (isMobile()) {
        if (message?.id) {
          forceCurrentScrollSection(message.id, message);
        } else {
          console.warn('message?.id undefined, cannot answer to the thread or message.');
        }
      } else {
        setIsEditing(true);
      }
    }, [forceCurrentScrollSection, message]);

    const cancelEditing = useCallback(() => {
      setIsEditing(false);
    }, []);

    const submitEdit = useCallback(
      async (values: CreateOrUpdateMessageVariablesSmall) => {
        await submitMessage(slug, message?.id, client, values, false);
        setIsEditing(false);
      },
      [slug, message?.id, client],
    );

    const hasBubbleFlowVisible = useAppSelector(
      s => !!messagesStateToChildMessagesIds(s.threads.messages, messageId, true).length,
    );

    if (!message?.id && !isBrowser) {
      // For client-side rendering, it happens when signing in/out from the topic page.
      // While it doesn't seem super clean, there would be probably a lot to refactor to fix it, and it currently works.
      console.warn('Server-side rendering has a missing message or message ID.');
      return null;
    }

    return (
      <div
        className={classnames(
          'ThreadMessageBlock px-3 md:px-5 pt-3 md:pt-5', // reduced bottom padding because the bottom-left action buttons include a padding of 12px.
          !hasBubbleFlowVisible && 'pb-2',
          isNestedAnswer ? 'isNestedAnswer' : 'isNotNestedAnswer',
        )}
      >
        {!hide &&
          (isEditing ? (
            // When editing an existing message
            <ThreadEditorForm
              baseId={contentEditorId}
              message={message as Message}
              onSubmitAnswer={submitEdit}
              onCancelAnswer={cancelEditing}
            />
          ) : (
            <div
              className={classnames(
                'ThreadInteractiveMessage',
                isTargeted ? 'isTargetedId bg-gray-lightest' : 'isNotTargetedId bg-white',
              )}
            >
              <ThreadInteractiveMessage
                messageId={messageId}
                answer={onAnswer}
                startEditing={startEditing}
                voteRef={voteRef}
                unlimited={unlimited}
                withTopIcon={false}
                isNestedAnswer={isNestedAnswer}
                mode="nested"
                onDelete={onDelete}
                isYakliDisplay={isYakliDisplay}
              />
            </div>
          ))}
      </div>
    );
  },
);
